import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ConfirmDialog from '../ConfirmDialog';
import Button from '../ui/Button';
import { Input } from '../pilot-ui/input';
import { ToggleGroup, ToggleGroupItem } from '../pilot-ui/toggle-group';
import { useAuth } from '../../context/AuthContext';
import { extractErrorMessage } from '../../utils/extractErrorMessage';
import AppLogsTimeline from './AppLogsTimeline';
import SessionFacts from './SessionFacts';
import SetupSummary from './SetupSummary';
import {
  DeviceLogEntry,
  DeviceLogSession,
  deleteAllForDevice,
  deleteSession,
  fetchOrphanedSessions,
  fetchSessionTimeline,
  fetchSessionsForDevice,
  fetchSessionsForUser,
} from './deviceLogs';

type SessionSource = 'device' | 'user' | 'orphaned';

interface Props {
  deviceName: string;
  // Firebase uid of the app user this device is enriched to, when known. Enables the
  // "orphaned logs for this user" bridge for recovering pre-serial setup sessions.
  userId?: string;
}

const LEVELS: DeviceLogEntry['level'][] = ['debug', 'info', 'warn', 'error'];
const SOURCES = ['ble', 'wifi', 'firestore', 'setup', 'app', 'router'];

const LEVEL_BADGE: Record<string, string> = {
  debug: 'text-gray-600',
  info: 'text-blue-700',
  warn: 'text-status-warning-foreground',
  error: 'text-red-700',
};

// Tooltip copy. Native `title=` (codebase precedent: DeviceDebug, DeviceSelector).
const LEVEL_HELP: Record<string, string> = {
  debug: 'Verbose tracing — navigation, raw opcode bytes. Noise unless drilling into a specific failure.',
  info: 'Normal milestones — connected, credential write accepted, activation online.',
  warn: 'Recoverable trouble — retry, disconnect, scan found no Clips, status timeout.',
  error: 'Failure — connect failed (terminal), connection error, setup connection failed.',
};

const SOURCE_HELP: Record<string, string> = {
  ble: 'Bluetooth — connect/disconnect, scan, opcode send/ack/retry/timeout, parse failures.',
  wifi: 'WiFi provisioning — SSID list, credential write (password masked), IoT activation polling.',
  firestore: 'Firestore reads/writes from the app — location / HVAC / filter documents.',
  setup: 'Setup flow lifecycle — session start, screen transitions, verify-screen polling.',
  app: 'App lifecycle + caught initialization / Firestore errors.',
  router: 'Screen navigation — push / pop / replace transitions.',
};

// Shared explanation of how a log becomes "orphaned" (deviceName == null).
const ORPHAN_WHY =
  'A log is "orphaned" when deviceName is null — no Clip serial is attached. Two ways that happens:\n' +
  "  1. Pre-serial — the entry was written before the app read the Clip's 24-hex serial over BLE. " +
  'Later entries in the SAME session pick up the serial once it is discovered.\n' +
  '  2. Never paired — setup aborted or failed before BLE connected at all (scan found nothing, ' +
  'connection error, user backed out), so deviceName stays null for the whole session.\n' +
  'Orphaned logs are invisible to device-centric browsing — recover them by user (uid) or the recent-orphaned feed.';

const SOURCE_TOGGLE_HELP: Record<SessionSource, string> = {
  device:
    "Sessions whose entries carry THIS Clip's serial (deviceName == selected device). " +
    'Includes setup sessions that reached BLE serial discovery, plus later runtime logs.',
  user:
    'All sessions for the app user this device is registered to (Firebase uid from enrichment), ' +
    "INCLUDING sessions that never got a Clip serial. Use when a user's setup failed before pairing — " +
    'recover their logs by identity instead of by device.\n\n' +
    ORPHAN_WHY,
  orphaned:
    'Recent sessions across ALL users where no Clip serial was ever attached (deviceName == null). ' +
    'Use when you know "a setup just failed" but not which device or user.\n\n' +
    ORPHAN_WHY,
};

const AppLogsSection: React.FC<Props> = ({ deviceName, userId }) => {
  // FS-868: deleting log entries deletes Firestore documents, which needs
  // firestore_write as well as the debug permission that opens this tab.
  const { hasPermission } = useAuth();
  const canDelete = hasPermission('firestore_write');
  const [sessionSource, setSessionSource] = useState<SessionSource>('device');
  const [sessions, setSessions] = useState<DeviceLogSession[]>([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [sessionsError, setSessionsError] = useState<string | null>(null);

  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(null);
  const [entries, setEntries] = useState<DeviceLogEntry[]>([]);
  const [entriesLoading, setEntriesLoading] = useState(false);
  const [entriesError, setEntriesError] = useState<string | null>(null);

  const [levelFilter, setLevelFilter] = useState<Set<string>>(new Set(LEVELS));
  const [sourceFilter, setSourceFilter] = useState<Set<string>>(new Set(SOURCES));
  const [textFilter, setTextFilter] = useState('');

  const [confirm, setConfirm] = useState<{
    title: string;
    message: string;
    onConfirm: () => Promise<void>;
  } | null>(null);
  const [actionBusy, setActionBusy] = useState(false);
  const [confirmError, setConfirmError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const sourceButtonRef = useRef<HTMLButtonElement>(null);
  const focusAfterDeleteRef = useRef<HTMLElement | null>(null);

  const reloadSessions = useCallback(async (): Promise<DeviceLogSession[] | null> => {
    setSessionsLoading(true);
    setSessionsError(null);
    try {
      let list: DeviceLogSession[];
      if (sessionSource === 'user') {
        list = userId ? await fetchSessionsForUser(userId) : [];
      } else if (sessionSource === 'orphaned') {
        list = await fetchOrphanedSessions();
      } else {
        list = await fetchSessionsForDevice(deviceName);
      }
      setSessions(list);
      if (list.length > 0 && !list.find((s) => s.sessionId === selectedSessionId)) {
        setSelectedSessionId(list[0].sessionId);
      }
      if (list.length === 0) setSelectedSessionId(null);
      return list;
    } catch (err: any) {
      setSessionsError(err?.response?.data?.message ?? err?.message ?? 'Failed to load sessions');
      return null;
    } finally {
      setSessionsLoading(false);
    }
  }, [deviceName, userId, sessionSource, selectedSessionId]);

  // Selecting a different device resets back to the device session source.
  useEffect(() => {
    setSessionSource('device');
    setSelectedSessionId(null);
    setEntries([]);
  }, [deviceName]);

  // Reload whenever the device or the active session source changes.
  useEffect(() => {
    reloadSessions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceName, sessionSource]);

  useEffect(() => {
    if (!selectedSessionId) {
      setEntries([]);
      return;
    }
    let cancelled = false;
    setEntriesLoading(true);
    setEntriesError(null);
    fetchSessionTimeline(selectedSessionId)
      .then((rows) => {
        if (!cancelled) setEntries(rows);
      })
      .catch((err: any) => {
        if (!cancelled)
          setEntriesError(err?.response?.data?.message ?? err?.message ?? 'Failed to load entries');
      })
      .finally(() => {
        if (!cancelled) setEntriesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedSessionId]);

  // msg + serialized ctx, lower-cased once per load — the structured Plan 104
  // fields (event/result/opcode/…) live in ctx, so search must cover it.
  const searchable = useMemo(
    () =>
      new Map(
        entries.map((e) => [
          e.id,
          `${e.msg}\n${e.ctx ? JSON.stringify(e.ctx) : ''}`.toLowerCase(),
        ]),
      ),
    [entries],
  );

  const searchNeedle = textFilter.trim().toLowerCase();

  const visibleEntries = useMemo(() => {
    return entries.filter((e) => {
      if (!levelFilter.has(e.level)) return false;
      if (!sourceFilter.has(e.source)) return false;
      if (searchNeedle && !searchable.get(e.id)?.includes(searchNeedle)) return false;
      return true;
    });
  }, [entries, searchable, levelFilter, sourceFilter, searchNeedle]);

  const finishDeletion = async () => {
    const refreshed = await reloadSessions();
    focusAfterDeleteRef.current = refreshed?.length ? null : sourceButtonRef.current;
    setConfirm(null);
  };

  const askDeleteSession = () => {
    if (!selectedSessionId) return;
    focusAfterDeleteRef.current = null;
    setConfirmError(null);
    const sid = selectedSessionId;
    setConfirm({
      title: 'Delete this session',
      message: `Delete every entry for session ${sid}? This cannot be undone.`,
      onConfirm: async () => {
        setConfirmError(null);
        setActionBusy(true);
        try {
          const n = await deleteSession(sid);
          setToast(`Deleted ${n} entries`);
          await finishDeletion();
        } catch (err: unknown) {
          setConfirmError(extractErrorMessage(err, 'Delete failed'));
        } finally {
          setActionBusy(false);
        }
      },
    });
  };

  const askDeleteAll = () => {
    focusAfterDeleteRef.current = null;
    setConfirmError(null);
    setConfirm({
      title: 'Delete all logs for this device',
      message: `Delete every Firestore log entry attached to ${deviceName}? This cannot be undone.`,
      onConfirm: async () => {
        setConfirmError(null);
        setActionBusy(true);
        try {
          const n = await deleteAllForDevice(deviceName);
          setToast(`Deleted ${n} entries`);
          await finishDeletion();
        } catch (err: unknown) {
          setConfirmError(extractErrorMessage(err, 'Delete failed'));
        } finally {
          setActionBusy(false);
        }
      },
    });
  };

  const switchSource = (src: SessionSource) => {
    if (src === sessionSource) return;
    setSelectedSessionId(null);
    setEntries([]);
    setSessionSource(src);
  };

  const emptyMessage =
    sessionSource === 'user'
      ? 'No sessions for this user yet.'
      : sessionSource === 'orphaned'
        ? 'No recent orphaned sessions.'
        : 'No sessions for this device yet.';

  return (
    <div className="mt-6 p-4 bg-card border border-border rounded-lg shadow-md">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <h3 className="text-lg font-semibold">App Logs</h3>
        <Button
          variant="primary"
          onClick={reloadSessions}
          disabled={sessionsLoading}
          title="Reload the session list for the currently selected source."
        >
          {sessionsLoading ? 'Loading...' : 'Refresh'}
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-3">
        <span
          className="text-xs text-muted-foreground cursor-help underline decoration-dotted"
          title={`Where to pull sessions from.\n\n${ORPHAN_WHY}`}
        >
          Source:
        </span>
        <ToggleGroup value={[sessionSource]} onValueChange={(values) => {
          const next = values[0] ?? sessionSource;
          if (next === 'device' || next === 'user' || next === 'orphaned') switchSource(next);
        }} variant="outline" aria-label="App log session source" aria-describedby="session-source-help" className="flex-wrap">
        <ToggleGroupItem ref={sourceButtonRef} value="device" title={SOURCE_TOGGLE_HELP.device}>This device</ToggleGroupItem>
        {userId && (
          <ToggleGroupItem value="user" title={SOURCE_TOGGLE_HELP.user}>orphaned logs for this user</ToggleGroupItem>
        )}
        <ToggleGroupItem value="orphaned" title={SOURCE_TOGGLE_HELP.orphaned}>Recent orphaned (ops)</ToggleGroupItem>
        </ToggleGroup>
        <p id="session-source-help" className="text-xs text-muted-foreground">Choose whose app sessions appear below.</p>
      </div>

      {toast && (
        <div className="mb-3 text-sm text-gray-700 bg-gray-50 border border-border rounded-sm p-2 flex justify-between">
          <span>{toast}</span>
          <Button variant="link" size="sm" onClick={() => setToast(null)} title="Hide this message">
            dismiss
          </Button>
        </div>
      )}

      <div className="mb-4 border border-border rounded-sm p-3">
        <div className="text-sm font-semibold mb-2">Sessions</div>
        {sessionsError && (
          <div role="alert" className="text-sm text-status-error-foreground mb-2">{sessionsError}</div>
        )}
        {sessions.length === 0 && !sessionsLoading && (
          <div className="text-sm text-muted-foreground">{emptyMessage}</div>
        )}
        <div className="flex flex-wrap gap-2 max-h-40 overflow-y-auto">
          {sessions.map((s) => {
            const active = s.sessionId === selectedSessionId;
            return (
              <div
                key={s.sessionId}
                onClick={() => setSelectedSessionId(s.sessionId)}
                className={`cursor-pointer rounded-lg border border-control-border p-2 text-xs ${active ? 'bg-secondary ring-2 ring-primary' : 'bg-card'}`}
              >
                <div className="flex items-baseline gap-2 whitespace-nowrap">
                  <span>{s.startTs.slice(0, 19).replace('T', ' ')}</span>
                  <span className={LEVEL_BADGE[s.maxLevel]}>{s.maxLevel}</span>
                  {s.deviceName === null && (
                    <span
                      className="text-status-warning-foreground font-semibold cursor-help"
                      title={`This session has no Clip serial (deviceName = null).\n\n${ORPHAN_WHY}`}
                    >
                      orphaned
                    </span>
                  )}
                </div>
                <div className="text-gray-600 whitespace-nowrap">
                  {s.entryCount} entries · {s.platform || '?'} {s.appVersion || ''}
                </div>
                {sessionSource !== 'device' && (
                  <div className="text-gray-600 whitespace-nowrap">uid {s.userId}</div>
                )}
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={(event) => {
                    event.stopPropagation();
                    setSelectedSessionId(s.sessionId);
                  }}
                  title={`Show log entries for the session starting ${s.startTs}`}
                  aria-pressed={active}
                >
                  View session
                </Button>
              </div>
            );
          })}
        </div>
      </div>

      <div>
        <div className="flex flex-wrap items-end gap-3 mb-3">
            <div>
              <div
                className="text-xs text-gray-600 mb-1 cursor-help underline decoration-dotted"
                title="Show only these severities. Highlighted = shown. Applied in the browser after the session loads — does not re-query."
              >
                Levels
              </div>
              <ToggleGroup multiple value={[...levelFilter]} onValueChange={(values) => setLevelFilter(new Set(values))} variant="outline" aria-label="Log levels" aria-describedby="log-level-help" className="flex-wrap">
                {LEVELS.map((lvl) => (
                  <ToggleGroupItem
                    key={lvl}
                    value={lvl}
                    title={LEVEL_HELP[lvl]}
                  >
                    {lvl}
                  </ToggleGroupItem>
                ))}
              </ToggleGroup>
              <p id="log-level-help" className="text-xs text-muted-foreground">Select severities to include in the displayed entries.</p>
            </div>
            <div>
              <div
                className="text-xs text-gray-600 mb-1 cursor-help underline decoration-dotted"
                title="Show only these log sources (the app subsystem that emitted the line). Highlighted = shown. Applied in the browser after the session loads."
              >
                Sources
              </div>
              <ToggleGroup multiple value={[...sourceFilter]} onValueChange={(values) => setSourceFilter(new Set(values))} variant="outline" aria-label="Log sources" aria-describedby="log-sources-help" className="flex-wrap">
                {SOURCES.map((src) => (
                  <ToggleGroupItem
                    key={src}
                    value={src}
                    title={SOURCE_HELP[src]}
                  >
                    {src}
                  </ToggleGroupItem>
                ))}
              </ToggleGroup>
              <p id="log-sources-help" className="text-xs text-muted-foreground">Select app subsystems to include in the displayed entries.</p>
            </div>
            <div className="flex-1 min-w-48">
              <label htmlFor="log-search" className="block text-xs text-gray-600 mb-1">Search msg + ctx</label>
              <Input
                id="log-search"
                type="text"
                value={textFilter}
                onChange={(e) => setTextFilter(e.target.value)}
                placeholder="substring match (msg or ctx)"
                title="Case-insensitive substring match against the message text and the entry's ctx JSON (event, result, opcode, …). Client-side only — Firestore can't full-text search, so it filters the already-loaded session."
                aria-describedby="log-search-help"
              />
              <p id="log-search-help" className="text-xs text-muted-foreground">Filter loaded entries by message or context text.</p>
            </div>
            {canDelete && (
              <div className="flex gap-2">
                <Button
                  variant="danger"
                  onClick={askDeleteSession}
                  disabled={!selectedSessionId || actionBusy}
                  title="Permanently delete every entry in the selected session (batched server-side). Cannot be undone. Otherwise entries auto-expire via the 30-day TTL."
                >
                  Delete session
                </Button>
                {sessionSource === 'device' && (
                  <Button
                    variant="danger"
                    onClick={askDeleteAll}
                    disabled={sessions.length === 0 || actionBusy}
                    title="Permanently delete EVERY log entry attached to this Clip serial, across all sessions. Cannot be undone. (Device source only.)"
                  >
                    Delete all for device
                  </Button>
                )}
              </div>
            )}
          </div>

        {entriesError && (
          <div role="alert" className="text-sm text-status-error-foreground mb-2">{entriesError}</div>
        )}
        {entriesLoading ? (
          <div className="text-sm text-muted-foreground">Loading entries...</div>
        ) : (
          <>
            <SetupSummary entries={entries} />
            <SessionFacts entries={entries} />
            <AppLogsTimeline entries={visibleEntries} searchNeedle={searchNeedle} />
          </>
        )}
      </div>

      <ConfirmDialog
        open={!!confirm}
        returnFocusTo={focusAfterDeleteRef}
        title={confirm?.title ?? ''}
        message={confirm?.message}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        tone="danger"
        submitting={actionBusy}
        error={confirmError}
        onConfirm={() => confirm?.onConfirm()}
        onCancel={() => {
          if (actionBusy) return;
          setConfirmError(null);
          setConfirm(null);
        }}
      />
    </div>
  );
};

export default AppLogsSection;
